import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';

import { apiClient, type AuditFilters, type AuditStats } from '@/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from '@/components/ui/tooltip';

const RANGE_OPTIONS = [
  { label: '24h', days: 1 },
  { label: '7d', days: 7 },
  { label: '30d', days: 30 },
  { label: '90d', days: 90 }
];

const STAT_CARDS: { key: keyof AuditStats['summary']; label: string }[] = [
  { key: 'total', label: 'Total events' },
  { key: 'logins', label: 'Logins' },
  { key: 'failed_logins', label: 'Failed logins' },
  { key: 'active_users', label: 'Active users' },
  { key: 'exports', label: 'Exports' }
];

function StatCard({ label, value }: { label: string; value: number }) {
  return (
    <Card>
      <CardContent className="p-4">
        <div className="text-xs uppercase tracking-wide text-muted-foreground">{label}</div>
        <div className="mt-1 font-mono text-2xl font-semibold tabular-nums">{value.toLocaleString()}</div>
      </CardContent>
    </Card>
  );
}

function EventTypeBars({ rows }: { rows: AuditStats['by_event_type'] }) {
  const max = Math.max(...rows.map((row) => row.count), 1);
  if (!rows.length) return <div className="text-sm text-muted-foreground">No events in range.</div>;
  return (
    <div className="space-y-1.5">
      {rows.map((row) => (
        <div key={row.event_type} className="flex items-center gap-2">
          <span className="w-40 shrink-0 truncate text-xs">{row.event_type}</span>
          <div className="h-4 flex-1 rounded bg-muted">
            <div className="h-full rounded bg-primary/70" style={{ width: `${(row.count / max) * 100}%` }} />
          </div>
          <span className="w-12 shrink-0 text-right font-mono text-xs tabular-nums">{row.count}</span>
        </div>
      ))}
    </div>
  );
}

function shortDay(day: string): string {
  return new Date(`${day}T00:00:00Z`).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC'
  });
}

function ActivityChart({ rows }: { rows: AuditStats['by_day'] }) {
  if (!rows.length) return <div className="text-sm text-muted-foreground">No activity in range.</div>;
  const max = Math.max(...rows.map((row) => row.count), 1);
  const total = rows.reduce((sum, row) => sum + row.count, 0);
  const peak = rows.reduce((best, row) => (row.count > best.count ? row : best), rows[0]);
  const points = rows.length === 1 ? [rows[0], rows[0]] : rows;
  const span = points.length - 1;
  const line = points.map((row, i) => `${i},${100 - (row.count / max) * 100}`).join(' ');
  const area = `0,100 ${line} ${span},100`;
  const mid = Math.floor((rows.length - 1) / 2);

  return (
    <div className="space-y-2">
      <div className="flex items-baseline justify-between text-xs text-muted-foreground">
        <span className="font-mono tabular-nums">{total.toLocaleString()} events</span>
        <span className="font-mono tabular-nums">
          peak {peak.count.toLocaleString()} · {shortDay(peak.day)}
        </span>
      </div>
      <div className="flex gap-2">
        <div className="flex w-9 shrink-0 flex-col justify-between py-0.5 text-right text-[10px] tabular-nums text-muted-foreground">
          <span>{max.toLocaleString()}</span>
          <span>0</span>
        </div>
        <div className="relative h-40 flex-1 text-primary">
          <svg
            className="absolute inset-0 h-full w-full overflow-visible"
            viewBox={`0 0 ${span} 100`}
            preserveAspectRatio="none"
          >
            <defs>
              <linearGradient id="auditActivityFill" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="currentColor" stopOpacity="0.35" />
                <stop offset="100%" stopColor="currentColor" stopOpacity="0.02" />
              </linearGradient>
            </defs>
            {[0, 25, 50, 75, 100].map((y) => (
              <line
                key={y}
                x1="0"
                x2={span}
                y1={y}
                y2={y}
                stroke="currentColor"
                strokeOpacity="0.1"
                strokeWidth="1"
                vectorEffect="non-scaling-stroke"
              />
            ))}
            <polygon points={area} fill="url(#auditActivityFill)" />
            <polyline
              points={line}
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinejoin="round"
              strokeLinecap="round"
              vectorEffect="non-scaling-stroke"
            />
          </svg>
          <TooltipProvider delayDuration={80}>
            <div className="absolute inset-0 flex">
              {rows.map((row) => (
                <Tooltip key={row.day}>
                  <TooltipTrigger asChild>
                    <div className="h-full flex-1 hover:bg-primary/10" />
                  </TooltipTrigger>
                  <TooltipContent side="top" className="font-mono text-xs tabular-nums">
                    {shortDay(row.day)} · {row.count}
                  </TooltipContent>
                </Tooltip>
              ))}
            </div>
          </TooltipProvider>
        </div>
      </div>
      <div className="flex gap-2 text-[10px] tabular-nums text-muted-foreground">
        <div className="w-9 shrink-0" />
        <div className="flex flex-1 justify-between">
          <span>{shortDay(rows[0].day)}</span>
          {rows.length > 2 ? <span>{shortDay(rows[mid].day)}</span> : null}
          <span>{shortDay(rows[rows.length - 1].day)}</span>
        </div>
      </div>
    </div>
  );
}

function TopTable({
  rows,
  onSelect
}: {
  rows: { label: string; count: number }[];
  onSelect?: (label: string) => void;
}) {
  if (!rows.length) return <div className="text-sm text-muted-foreground">No data in range.</div>;
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Name</TableHead>
          <TableHead className="text-right">Events</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map((row) => (
          <TableRow
            key={row.label}
            className={onSelect ? 'cursor-pointer hover:bg-muted/50' : undefined}
            onClick={onSelect ? () => onSelect(row.label) : undefined}
            title={onSelect ? 'Filter events by this' : undefined}
          >
            <TableCell className="max-w-[220px] truncate">{row.label}</TableCell>
            <TableCell className="text-right font-mono tabular-nums">{row.count}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

/** Audit-log overview: stat cards, event-type + activity charts, top users / IPs. */
export default function AuditOverview({
  onDrilldown
}: {
  onDrilldown?: (patch: Partial<AuditFilters>) => void;
}) {
  const [days, setDays] = useState(30);
  const [stats, setStats] = useState<AuditStats | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    setLoading(true);
    const since = new Date(Date.now() - days * 86_400_000).toISOString();
    apiClient
      .getAuditStats(since)
      .then((data) => {
        if (active) setStats(data);
      })
      .catch((err) => toast.error('Failed to load audit stats', { description: String(err) }))
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [days]);

  const userRows = useMemo(
    () => (stats?.top_users ?? []).map((row) => ({ label: row.identifier, count: row.count })),
    [stats]
  );
  const ipRows = useMemo(
    () => (stats?.top_source_ips ?? []).map((row) => ({ label: row.ip, count: row.count })),
    [stats]
  );

  return (
    <div className="space-y-4">
      <div className="flex gap-1">
        {RANGE_OPTIONS.map((option) => (
          <Button
            key={option.days}
            variant={days === option.days ? 'default' : 'outline'}
            size="sm"
            onClick={() => setDays(option.days)}
          >
            {option.label}
          </Button>
        ))}
      </div>
      {loading || !stats ? (
        <Skeleton className="h-64 w-full" />
      ) : (
        <>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-5">
            {STAT_CARDS.map((card) => (
              <StatCard key={card.key} label={card.label} value={stats.summary[card.key] ?? 0} />
            ))}
          </div>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">Activity over time</CardTitle>
            </CardHeader>
            <CardContent>
              <ActivityChart rows={stats.by_day} />
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">Events by type</CardTitle>
            </CardHeader>
            <CardContent>
              <EventTypeBars rows={stats.by_event_type} />
            </CardContent>
          </Card>
          <div className="grid gap-4 lg:grid-cols-2">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">Top users</CardTitle>
              </CardHeader>
              <CardContent>
                <TopTable
                  rows={userRows}
                  onSelect={(label) => onDrilldown?.({ user_identifier: label, ip: undefined })}
                />
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">Top source IPs</CardTitle>
              </CardHeader>
              <CardContent>
                <TopTable
                  rows={ipRows}
                  onSelect={(label) => onDrilldown?.({ ip: label, user_identifier: undefined })}
                />
              </CardContent>
            </Card>
          </div>
        </>
      )}
    </div>
  );
}
